import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { isWholeNumber } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { emailBuilderExit, emailBuilderFlushAutosave, emailBuilderLoad } from "redux/actions/emailBuilder/emailBuilder";
import { loadAdminFacility } from "redux/actions/facility";
import { isEmailBuilderEditorLoadGateReady } from "./operations/emailBuilderEditorLoadGate";
import { editorReduxStore, runEditorThunk } from "./domain/store";
import { editorThunks } from "./domain/redux/thunks";
import { emailDocumentsEqual } from "./emailBuilderAutosaveBaseline";
import {
  decideEmailBuilderBackNavigation,
  emailBuilderHasPendingAutosave,
} from "./emailBuilderExitLifecycle";
import {
  emailCampaignSelectTemplatePath,
  isEmailBuilderTemplateRoute,
  resolveEmailBuilderReturnTo,
  type TEmailBuilderWorkspace,
} from "./emailBuilderWorkspace";
import { EmailBuilderPageStatus } from "./EmailBuilderPageStatus";
import { EmailBuilderShell } from "./shell/EmailBuilderShell";
import "./emailBuilder.scss";

interface IEmailBuilderParams {
  id: string;
}

interface IEmailBuilderWorkspaceProps {
  workspace: TEmailBuilderWorkspace;
  onBack: () => void;
}

function EmailBuilderWorkspace(props: IEmailBuilderWorkspaceProps) {
  const { workspace, onBack } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const loadStatus = useAppSelector(store => store.emailBuilderStore.loadStatus);
  const loadError = useAppSelector(store => store.emailBuilderStore.loadError);
  const document = useAppSelector(store => store.emailBuilderStore.document);
  const unavailableLibraryMediaRepairedCount = useAppSelector(
    store => store.emailBuilderStore.unavailableLibraryMediaRepairedCount,
  );
  const workspaceFromStore = useAppSelector(store => store.emailBuilderStore.workspace);
  const facilityIsLoaded = useAppSelector(store => store.facilityStore.isLoaded);

  const workspaceId = workspace.id;
  const workspaceKind = workspace.kind;
  const [editorHydrated, setEditorHydrated] = useState(false);
  const repairedMediaSaveTriggeredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const returnState = location.state;

    void (async () => {
      const outcome = await dispatch(
        emailBuilderLoad({ kind: workspaceKind, id: workspaceId }),
      );
      if (cancelled || outcome !== "select_template" || workspaceKind !== "campaign") {
        return;
      }

      history.replace(emailCampaignSelectTemplatePath(workspaceId), returnState);
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, history, location.state, workspaceId, workspaceKind]);

  useEffect(() => {
    repairedMediaSaveTriggeredRef.current = false;
  }, [workspaceId, workspaceKind]);

  useEffect(() => {
    if (
      !editorHydrated ||
      unavailableLibraryMediaRepairedCount === 0 ||
      repairedMediaSaveTriggeredRef.current
    ) {
      return;
    }

    repairedMediaSaveTriggeredRef.current = true;
    void dispatch(emailBuilderFlushAutosave());
  }, [dispatch, editorHydrated, unavailableLibraryMediaRepairedCount]);

  useLayoutEffect(() => {
    if (
      loadStatus !== "succeeded" ||
      document === null ||
      workspaceFromStore?.id !== workspaceId ||
      workspaceFromStore?.kind !== workspaceKind
    ) {
      setEditorHydrated(false);
      return;
    }

    if (
      !isEmailBuilderEditorLoadGateReady(document, {
        facilityIsLoaded,
      })
    ) {
      setEditorHydrated(false);
      return;
    }

    const editorDocument = editorReduxStore.getState().editor.document;

    if (!emailDocumentsEqual(document, editorDocument)) {
      runEditorThunk(editorThunks.loadDocument(document, "blank"));
    }

    setEditorHydrated(true);
  }, [
    document,
    facilityIsLoaded,
    loadStatus,
    workspaceId,
    workspaceKind,
    workspaceFromStore?.id,
    workspaceFromStore?.kind,
  ]);

  if (loadStatus === "failed") {
    const fallbackMessage = t("Could not load email builder.");
    const isUnsupportedSchemaVersion =
      loadError !== null && /Unsupported schemaVersion\b/.test(loadError);

    return (
      <EmailBuilderPageStatus
        title={t(
          isUnsupportedSchemaVersion
            ? "This email can't be opened in the editor"
            : "Couldn't load email builder",
        )}
        description={
          isUnsupportedSchemaVersion
            ? t(
                "This design uses an unsupported schema version and cannot be opened. Go back and create a new email from a blank layout or template. Contact support if you need help recovering content from this design.",
              )
            : loadError !== null
              ? t(loadError)
              : fallbackMessage
        }
        detail={isUnsupportedSchemaVersion ? loadError : null}
        primaryActionLabel={t("Back")}
        onPrimaryAction={onBack}
      />
    );
  }

  const canvasLoading =
    loadStatus === "standby" || loadStatus === "loading" || !editorHydrated;

  return (
    <EmailBuilderShell
      onBack={onBack}
      canvasLoading={canvasLoading}
      editorSyncReady={editorHydrated}
    />
  );
}

export default function EmailBuilder() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { id: ownerIdParam } = useParams<IEmailBuilderParams>();
  const isFlushingBackRef = useRef(false);
  const autoSaveStatus = useAppSelector(store => store.emailBuilderStore.autoSaveStatus);
  const autoSaveConflict = useAppSelector(store => store.emailBuilderStore.autoSaveConflict);

  const workspace: TEmailBuilderWorkspace | null = isWholeNumber(ownerIdParam)
    ? {
        kind: isEmailBuilderTemplateRoute(location.pathname) ? "template" : "campaign",
        id: Number(ownerIdParam),
      }
    : null;
  const workspaceId = workspace?.id;
  const workspaceKind = workspace?.kind;

  useEffect(() => {
    void dispatch(loadAdminFacility({ client: true }, false));
  }, [dispatch]);

  useEffect(() => {
    if (workspaceId === undefined || workspaceKind === undefined) {
      return;
    }

    return () => {
      dispatch(emailBuilderExit());
    };
  }, [dispatch, workspaceId, workspaceKind]);

  useEffect(() => {
    if (
      workspaceId === undefined ||
      workspaceKind === undefined ||
      !emailBuilderHasPendingAutosave(autoSaveStatus)
    ) {
      return;
    }

    function handleBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [autoSaveStatus, workspaceId, workspaceKind]);

  async function handleBack() {
    const decision = decideEmailBuilderBackNavigation({
      autoSaveStatus,
      isFlushing: isFlushingBackRef.current,
      autoSaveConflict,
    });

    if (decision.kind === "ignore-duplicate") {
      return;
    }

    function navigateAway() {
      if (workspace !== null) {
        history.push(resolveEmailBuilderReturnTo(workspace, location.state));
        return;
      }

      history.push("/admin/email-marketing");
    }

    if (decision.kind === "navigate") {
      navigateAway();
      return;
    }

    isFlushingBackRef.current = true;
    const saved = await emailBuilderFlushAutosave()(dispatch);
    isFlushingBackRef.current = false;

    if (!saved) {
      return;
    }

    navigateAway();
  }

  if (workspace === null) {
    const invalidOwner = isEmailBuilderTemplateRoute(location.pathname);
    return (
      <EmailBuilderPageStatus
        title={t(invalidOwner ? "Invalid template" : "Invalid campaign")}
        description={t("This link is not valid. Go back and open the email from your list.")}
        primaryActionLabel={t("Back")}
        onPrimaryAction={() => handleBack()}
      />
    );
  }

  return <EmailBuilderWorkspace workspace={workspace} onBack={handleBack} />;
}
